import { computed } from "vue";
import {
  isValidUrl,
  isValidUrlRule,
  isValidUrlRuleStrict,
  urlFieldErrorMessage,
  buildValidationContext,
} from "@/utils/triggerFormUtils";

/**
 * Derives regex/wildcard state and URL-field validators for a trigger being edited.
 *
 * @param {import("vue").Ref<object>} editingTrigger - Trigger being edited.
 * @param {import("vue").Ref<string>} triggerType - Current trigger type.
 * @returns {{
 *   regexAllowed: import("vue").ComputedRef<boolean>,
 *   testType: import("vue").ComputedRef<string>,
 *   testerLabel: import("vue").ComputedRef<string>,
 *   containsRegexOrWildcards: import("vue").ComputedRef<boolean>,
 *   isValidUrl: (str: string, useStrict: boolean) => boolean,
 *   isValidUrlRuleStrict: (str: string) => true|string,
 *   isValidUrlRule: (str: string) => true|string,
 *   urlFieldErrorMessage: (str: string, useStrict?: boolean) => string|null,
 * }}
 * @example
 * const { testType, isValidUrl } = useTriggerUrlValidation(editingTrigger, triggerType);
 */
export function useTriggerUrlValidation(editingTrigger, triggerType) {
  const regexAllowed = computed(() => triggerType.value !== "push_event");
  const testType = computed(() => (editingTrigger.value.use_regex ? "Regular" : "Wildcard"));
  const testerLabel = computed(() => `Test ${testType.value} expression`);

  const containsRegexOrWildcards = computed(() => {
    const { use_regex, url_path, query_params, url_hash, url_host } = editingTrigger.value;
    const urlStr = [url_path, query_params, url_hash, url_host].join();
    return (use_regex ? /[.*+?^${}()|[\]\\]/ : /\*/).test(urlStr);
  });

  const context = () => buildValidationContext(triggerType.value, editingTrigger.value.use_regex);

  return {
    regexAllowed,
    testType,
    testerLabel,
    containsRegexOrWildcards,
    isValidUrl: (str, useStrict) => isValidUrl(str, useStrict, context()),
    isValidUrlRuleStrict: str => isValidUrlRuleStrict(str, context()),
    isValidUrlRule: str => isValidUrlRule(str, context()),
    urlFieldErrorMessage: (str, useStrict = false) =>
      urlFieldErrorMessage(str, useStrict, context()),
  };
}
